<script>
import { mapState } from 'vuex'
import {
  NOTIFICATIONS_QUERY,
  UNREAD_NOTIFICATION_COUNT_QUERY,
} from '@src/graphql/queries'
import {
  MARK_NOTIFICATION_READ_MUTATION,
  MARK_ALL_NOTIFICATIONS_READ_MUTATION,
} from '@src/graphql/mutations'
import { notificationRoute } from '@utils/notification-link'

// Notification bell for the top bar: a badge with the unread count, and a
// dropdown listing the user's notifications. The badge count is polled so it
// stays fresh; the list itself is only fetched once the menu is opened.
export default {
  name: 'NotificationBell',
  data: () => ({
    menu: false,
    opened: false,
    notifications: [],
    unreadCount: 0,
  }),
  apollo: {
    unreadCount: {
      query: UNREAD_NOTIFICATION_COUNT_QUERY,
      update: (data) => data.unreadNotificationCount || 0,
      // Keep the badge current without the user reloading. Also the fallback for
      // cross-user updates (when someone else changes your schedule), since the
      // store `refresh` signal only fires for the acting user's own session.
      pollInterval: 15000,
      fetchPolicy: 'cache-and-network',
    },
    notifications: {
      query: NOTIFICATIONS_QUERY,
      // The bell shows a short preview; "View all notifications" opens the full
      // history page.
      variables: { limit: 10 },
      update: (data) => data.notifications || [],
      // Don't fetch the list until the bell is first opened.
      skip() {
        return !this.opened
      },
      fetchPolicy: 'cache-and-network',
    },
  },
  computed: {
    // Bumped by actions that may have created a notification (e.g. scheduling),
    // so the badge updates immediately instead of on the next poll.
    ...mapState('notifications', ['refreshTick']),
    // The app's own theme, "light" or "dark" ("Match my device" is resolved
    // before it is set). The header bar runs a local DARK theme for its white
    // icons, and this dropdown inherits it, so it was pinned to "light", which
    // then left it white in dark mode. `global` is the app-wide theme even here,
    // where `current` is the header bar's.
    appTheme() {
      const theme = this.$vuetify && this.$vuetify.theme
      const name = theme && theme.global && theme.global.name
      return name === 'dark' ? 'dark' : 'light'
    },
  },
  watch: {
    menu(open) {
      if (!open) return
      this.opened = true
      // Refetch the list each time it's opened (no-op on the very first open,
      // where un-skipping already triggers the initial fetch).
      const q = this.$apollo.queries.notifications
      // Fire-and-forget view refresh: a failure only means stale data, and the
      // Apollo error link already routes it through reportError. The catch is
      // here so a rejected refetch isn't an unhandled promise rejection.
      if (q) q.refetch().catch(() => {})
    },
    refreshTick() {
      // An external action asked us to refresh: update the badge now, and the
      // open list too if the menu is currently showing.
      this.refreshCount()
      if (this.opened) {
        const q = this.$apollo.queries.notifications
        if (q) q.refetch().catch(() => {})
      }
    },
  },
  methods: {
    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    },
    refreshCount() {
      const q = this.$apollo.queries.unreadCount
      if (q) q.refetch().catch(() => {})
    },
    notificationRoute(n) {
      return notificationRoute(n)
    },
    // A notification with somewhere to go opens it: a lockout assignment, the
    // machine (its procedures and the board). Reading it there marks it read.
    open(n) {
      const route = notificationRoute(n)
      if (!route) return
      this.markRead(n)
      this.menu = false
      this.$router.push(route).catch(() => {})
    },
    async markRead(n) {
      if (!n || n.read) return
      // Apollo query results are immutable (frozen), so mutating n.read in place
      // silently fails. Replace the item with a read copy instead. Optimistic;
      // the mutation persists it, and the badge refresh below reconciles.
      this.notifications = this.notifications.map((x) =>
        x.id === n.id ? { ...x, read: true } : x
      )
      try {
        await this.$apollo.mutate({
          mutation: MARK_NOTIFICATION_READ_MUTATION,
          variables: { id: n.id },
        })
      } catch (e) {
        // ignore. The badge refresh below reconciles state
      }
      this.refreshCount()
    },
    async markAllRead() {
      this.notifications = this.notifications.map((n) => ({ ...n, read: true }))
      try {
        await this.$apollo.mutate({
          mutation: MARK_ALL_NOTIFICATIONS_READ_MUTATION,
        })
      } catch (e) {
        // ignore. Best-effort
      }
      this.refreshCount()
    },
  },
}
</script>

<template>
  <v-menu v-model="menu" :close-on-content-click="false" location="bottom end">
    <template v-slot:activator="{ props }">
      <v-btn
        icon
        variant="text"
        color="white"
        v-bind="props"
        :aria-label="$t('components.notifications.title')"
      >
        <!-- White pill with brand-red count: a red badge disappears into the
             red app bar (it only read as a washed-out circle). -->
        <v-badge
          :model-value="unreadCount > 0"
          :content="unreadCount"
          color="white"
          text-color="brand"
          max="99"
        >
          <v-icon>$bell</v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card :theme="appTheme" min-width="320" max-width="420">
      <div class="d-flex align-center justify-space-between px-4 py-2">
        <span class="text-subtitle-1 font-weight-medium">{{
          $t('components.notifications.title')
        }}</span>
        <v-btn
          v-if="unreadCount > 0"
          size="small"
          variant="text"
          color="primary"
          @click="markAllRead"
          >{{ $t('components.notifications.markAllRead') }}</v-btn
        >
      </div>
      <v-divider />
      <v-list
        v-if="notifications.length"
        density="comfortable"
        max-height="420"
        class="overflow-y-auto py-0"
      >
        <template v-for="(n, i) in notifications" :key="n.id">
          <!-- Clickable when it leads somewhere: the machine a lockout
               assignment is about. -->
          <v-list-item
            :class="{ 'notification-unread': !n.read }"
            :link="!!notificationRoute(n)"
            data-test="notification"
            @click="open(n)"
          >
            <div class="d-flex">
              <!-- Unread indicator dot -->
              <div class="notification-dot-col">
                <span v-if="!n.read" class="notification-dot"></span>
              </div>
              <div class="flex-grow-1">
                <div class="text-body-2 font-weight-medium">{{ n.title }}</div>
                <div class="text-body-2 text-medium-emphasis notification-body">
                  {{ n.body }}
                </div>
                <div class="d-flex align-center justify-space-between mt-1">
                  <span class="text-caption text-medium-emphasis">
                    {{ formatDate(n.createdAt) }}
                  </span>
                  <div class="d-flex align-center">
                    <v-btn
                      v-if="notificationRoute(n)"
                      size="x-small"
                      variant="text"
                      color="primary"
                      data-test="notification-open"
                      @click.stop="open(n)"
                      >{{ $t('components.notifications.open') }}</v-btn
                    >
                    <v-btn
                      v-if="!n.read"
                      size="x-small"
                      variant="text"
                      color="primary"
                      @click.stop="markRead(n)"
                      >{{ $t('components.notifications.markRead') }}</v-btn
                    >
                  </div>
                </div>
              </div>
            </div>
          </v-list-item>
          <v-divider v-if="i < notifications.length - 1" />
        </template>
      </v-list>
      <div v-else class="text-medium-emphasis text-center py-8 px-4">
        {{ $t('components.notifications.allCaughtUp') }}
      </div>
      <v-divider />
      <v-btn
        block
        variant="text"
        color="primary"
        class="text-none"
        :to="{ name: 'notifications' }"
        @click="menu = false"
        >{{ $t('components.notifications.viewAll') }}</v-btn
      >
    </v-card>
  </v-menu>
</template>

<style scoped>
.notification-unread {
  background-color: rgba(var(--v-theme-primary), 0.08);
}
.notification-body {
  /* Preserve the line breaks in multi-line bodies (e.g. an appended "Notes:"). */
  white-space: pre-line;
}
.notification-dot-col {
  flex: 0 0 16px;
  padding-top: 6px;
}
.notification-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgb(var(--v-theme-primary));
}
</style>
